import { useEffect, useMemo } from 'react';

import { Close } from '@mui/icons-material';
import { IconButton, Slide } from '@mui/material';

import {
  Select,
  ListItem,
  Indicator,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { usePersistentState } from '@cast/utils';

import { features, FeatureKey } from './features/features';
import { useToolboxState } from './toolbox-state';

const featureKeys = features.map(({ title }) => title);

export const ToolboxPanel = () => {
  const { isOpen, toggle } = useToolboxState();
  const [selectedFeature, setSelectedFeature] = usePersistentState<FeatureKey>(
    'dev-toolbox-selected-feature',
    'Abilities'
  );

  const FeatureComponent = useMemo(
    () => features.find((f) => f.title === selectedFeature)?.component,
    [selectedFeature]
  );

  useEffect(() => {
    if (!FeatureComponent) {
      setSelectedFeature(features[0].title);
    }
  }, [FeatureComponent, setSelectedFeature]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        toggle();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, toggle]);

  return (
    <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
      <Stack
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '50vh',
          boxShadow: tokens.theme.shadows.elevation.medium,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: tokens.theme.bg.structural.surface,
          borderTop: `1px solid ${tokens.theme.border.structural.neutral}`,
          color: tokens.theme.fg.structural.primary,
          px: 16,
          zIndex: (theme) => theme.zIndex.modal,
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            py: 4,
          }}
        >
          <Typography variant="h6" component="h2">
            Dev Toolbox
          </Typography>
          <Select
            value={selectedFeature}
            onChange={(value) => {
              setSelectedFeature(value);
            }}
            renderOption={(option, listItemProps) => {
              const isActive = features
                .find((it) => it.title === option)
                ?.useState.getState()
                .getIsActive();

              return (
                <ListItem {...listItemProps}>
                  <Stack
                    direction="row"
                    gap={8}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="P14R">{option}</Typography>
                    {isActive && <Indicator size="dot" />}
                  </Stack>
                </ListItem>
              );
            }}
            options={featureKeys}
            inputProps={{ sx: { ml: 24 } }}
            size="sm"
          />
          <Box flexGrow={1} id="toolbox-header" pl={24} pr={12}></Box>
          <IconButton
            onClick={toggle}
            size="small"
            sx={{
              ml: 'auto',
              color: tokens.theme.fg.structural.secondary,
            }}
          >
            <Close />
          </IconButton>
        </Stack>
        <Box height="100%" position="relative">
          {FeatureComponent && <FeatureComponent />}
        </Box>
      </Stack>
    </Slide>
  );
};
